import React, { useEffect } from 'react';

import { classnames, useClassnames } from '@lumapps/classnames';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { EmptyState } from '@lumapps/lumx-states/components/EmptyState';
import { mdiClose, mdiPlayBoxMultiple } from '@lumapps/lumx/icons';
import {
    Alignment,
    AspectRatio,
    Emphasis,
    FlexBox,
    Heading,
    IconButton,
    Kind,
    Message,
    Orientation,
    Size,
    SkeletonRectangle,
    SkeletonRectangleVariant,
    Text,
    Typography,
} from '@lumapps/lumx/react';
import { PlaylistPickerDialog } from '@lumapps/play-pickers/components/PlaylistPickerDialog';
import { PLAY_PICKERS } from '@lumapps/play-pickers/keys';
import { BlockPlaylist } from '@lumapps/play-playlists/components/BlockPlaylist';
import { useFetchPlaylist } from '@lumapps/play-playlists/hooks/useFetchPlaylist';
import { Playlist } from '@lumapps/play-playlists/types';
import { isPlayVideoEnabled } from '@lumapps/play/ducks/selectors';
import { usePlayDataAttributes } from '@lumapps/play/hooks/usePlayDataAttributes';
import { PLAY } from '@lumapps/play/keys';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { WIDGET_PLAYLIST } from '../../../keys';
import { WidgetPlaylistProperties } from '../../../types';
import { getPlaylistIdForLanguage } from '../../../utils';

import './index.scss';

type PlaylistProperty = Pick<WidgetPlaylistProperties, 'playlist'>;

export interface PlaylistUploaderProps {
    properties: PlaylistProperty;
    hasError?: boolean;
    onChange: (properties: PlaylistProperty) => void;
    onPlaylistRemove: () => void;
}

const CLASSNAME = 'playlist-uploader';
const fetchKey = CLASSNAME;

/**
 *
 * @param PlaylistUploaderProps
 * @returns PlaylistUploader
 */
export const PlaylistUploader = ({
    properties,
    hasError = false,
    onChange,
    onPlaylistRemove,
}: PlaylistUploaderProps) => {
    const { translateKey, translateObject } = useTranslate();
    const { get } = usePlayDataAttributes();
    const [isPickerOpen, togglePicker, closePicker] = useBooleanState(false);
    const { block, element } = useClassnames(CLASSNAME);
    const { fetchPlaylist, playlist, status } = useFetchPlaylist({ fetchKey });
    const inputLanguage = useSelector(inputLanguageSelector);
    const playlistId = properties?.playlist
        ? getPlaylistIdForLanguage(properties.playlist, translateObject, true, inputLanguage)
        : undefined;

    const isPlayEnabled = useSelector(isPlayVideoEnabled);

    useEffect(() => {
        if (playlistId) {
            fetchPlaylist({
                fields: ['title', 'thumbnail'],
                id: playlistId,
            });
        }
    }, [fetchPlaylist, playlistId]);

    const handlePickPlaylist = (playlists: Playlist[]) => {
        const selectedPlaylistIds = playlists.map((p) => p.id);
        onChange({
            ...properties,
            playlist: {
                ...(typeof properties?.playlist === 'object' ? properties.playlist : {}),
                [inputLanguage]: {
                    ids: selectedPlaylistIds,
                },
            },
        });
        closePicker();
    };

    // Needed to display or not the PlaylistBlock
    const hasPlaylist = Boolean(playlistId);

    if (!isPlayEnabled) {
        return (
            <div className={CLASSNAME}>
                <FlexBox className={element('title')} orientation={Orientation.horizontal}>
                    <Heading as="h4" typography="subtitle1" className={element('subtitle')}>
                        {translateKey(WIDGET_PLAYLIST.SUBTITLE)}
                    </Heading>
                </FlexBox>

                <Message kind={Kind.warning} hasBackground>
                    <Text as="p" typography={Typography.body1}>
                        {translateKey(PLAY.UPLOAD_VIDEO_DISABLED)}
                    </Text>
                </Message>
            </div>
        );
    }

    return (
        <div className={block()}>
            <FlexBox className={element('title')} orientation={Orientation.horizontal}>
                <Heading as="h4" typography="subtitle1" className={element('subtitle')}>
                    {translateKey(WIDGET_PLAYLIST.SUBTITLE)}
                </Heading>
            </FlexBox>
            {status === BaseLoadingStatus.loading ? (
                <SkeletonRectangle aspectRatio={AspectRatio.wide} variant={SkeletonRectangleVariant.rounded} />
            ) : (
                <>
                    {hasError && (
                        <Message className={element('error-message')} kind={Kind.error} hasBackground>
                            {translateKey(WIDGET_PLAYLIST.PLAYLIST_NO_LONGER_AVAILABLE)}
                        </Message>
                    )}
                    {hasPlaylist ? (
                        <FlexBox
                            className={element('selected-playlist')}
                            orientation={Orientation.horizontal}
                            hAlign={Alignment.center}
                        >
                            {playlist && (
                                <BlockPlaylist
                                    playlist={playlist}
                                    orientation={Orientation.horizontal}
                                    alignment={Alignment.center}
                                >
                                    <BlockPlaylist.Figure size={Size.l}>
                                        <BlockPlaylist.Thumbnail withBadge />
                                    </BlockPlaylist.Figure>

                                    <BlockPlaylist.Content>
                                        <BlockPlaylist.Title as="span" truncate typography={Typography.body1} />
                                    </BlockPlaylist.Content>
                                </BlockPlaylist>
                            )}
                            <IconButton
                                label={translateKey(GLOBAL.REMOVE)}
                                className={element('remove-icon')}
                                size={Size.m}
                                emphasis={Emphasis.low}
                                icon={mdiClose}
                                onClick={onPlaylistRemove}
                                {...get({
                                    element: 'widget-remove-playlist-button',
                                    action: 'remove-playlist',
                                })}
                            />
                        </FlexBox>
                    ) : (
                        // This component is used as an uploader, we just need his style to fit the Figma
                        <EmptyState
                            wrapperProps={{
                                className: classnames(CLASSNAME, element('input')),
                            }}
                            iconProps={{
                                icon: mdiPlayBoxMultiple,
                                size: Size.m,
                            }}
                            message={translateKey(WIDGET_PLAYLIST.NO_PLAYLIST_SELECTED)}
                            buttonProps={{
                                emphasis: Emphasis.medium,
                                onClick: togglePicker,
                                children: translateKey(PLAY_PICKERS.SELECT_PLAYLIST),
                                ...get({
                                    element: 'widget-select-playlist-button',
                                    action: 'create',
                                }),
                            }}
                        />
                    )}
                </>
            )}

            <PlaylistPickerDialog
                isOpen={isPickerOpen}
                onSelectionConfirm={handlePickPlaylist}
                onSelectionCancel={closePicker}
            />
        </div>
    );
};
